import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { LawEnforcementProfile } from '../types';

interface LawEnforcementStats {
  total_profiles: number;
  pending_profiles: number;
  approved_profiles: number;
  rejected_profiles: number;
  auto_approved_profiles: number;
}

export const AdminLawEnforcementPage: React.FC = () => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<LawEnforcementProfile[]>([]);
  const [stats, setStats] = useState<LawEnforcementStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<LawEnforcementProfile | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    rejectionReason: '',
    adminNotes: ''
  });

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.request<{
        profiles: LawEnforcementProfile[];
        statistics: LawEnforcementStats;
      }>('/admin/law-enforcement/profiles/');
      setProfiles(response.profiles);
      setStats(response.statistics);
    } catch (err: any) {
      setError(err.message || 'Failed to load profiles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (profileId: number) => {
    try {
      await apiService.approveLawEnforcementProfile(profileId);
      setSuccess('Profile approved successfully');
      loadProfiles();
    } catch (err: any) {
      setError(err.message || 'Failed to approve profile');
    }
  };

  const handleReject = async (profileId: number) => {
    try {
      await apiService.rejectLawEnforcementProfile(profileId, reviewData);
      setSuccess('Profile rejected successfully');
      setShowReviewModal(false);
      setSelectedProfile(null);
      setReviewData({ rejectionReason: '', adminNotes: '' });
      loadProfiles();
    } catch (err: any) {
      setError(err.message || 'Failed to reject profile');
    }
  };

  const openReviewModal = (profile: LawEnforcementProfile) => {
    setSelectedProfile(profile);
    setShowReviewModal(true);
  };

  const openViewModal = (profile: LawEnforcementProfile) => {
    setSelectedProfile(profile);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedProfile(null);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'not_applied': { variant: 'secondary' as const, text: 'Not Applied' },
      'pending': { variant: 'warning' as const, text: 'Pending Review' },
      'approved': { variant: 'success' as const, text: 'Approved' },
      'rejected': { variant: 'danger' as const, text: 'Rejected' },
      'auto_approved': { variant: 'success' as const, text: 'Auto Approved' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_applied;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user?.isStaff) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-300">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Law Enforcement Management"
          subtitle="Review and manage law enforcement profile applications"
          align="center"
        />

        {/* Status Messages */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-green-800 dark:text-green-200">{success}</p>
          </div>
        )}

        {/* Statistics Dashboard */}
        {stats && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {stats.total_profiles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Profiles</div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {stats.pending_profiles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Pending Review</div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats.approved_profiles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Approved</div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {stats.rejected_profiles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Rejected</div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.auto_approved_profiles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Auto Approved</div>
              </div>
            </Card>
          </div>
        )}

        {/* Profiles List */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Law Enforcement Profiles ({profiles.length})
              </h2>
              <Button
                variant="outline"
                onClick={loadProfiles}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner text="Loading profiles..." />
              </div>
            ) : profiles.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No law enforcement profiles found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Applied
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {profiles.map((profile) => (
                      <tr key={profile.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              User ID: {profile.id}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Profile created: {formatDate(profile.createdAt)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {profile.departmentName || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {profile.rankPosition || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(profile.verificationStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(profile.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {profile.verificationStatus === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => handleApprove(profile.id)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => openReviewModal(profile)}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openViewModal(profile)}
                            >
                              View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        {/* Review Modal */}
        {showReviewModal && selectedProfile && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Reject Profile
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Rejecting law enforcement profile (ID: {selectedProfile.id})
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rejection Reason
                    </label>
                    <textarea
                      value={reviewData.rejectionReason}
                      onChange={(e) => setReviewData(prev => ({ ...prev, rejectionReason: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      rows={3}
                      placeholder="Enter reason for rejection..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Admin Notes
                    </label>
                    <textarea
                      value={reviewData.adminNotes}
                      onChange={(e) => setReviewData(prev => ({ ...prev, adminNotes: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      rows={2}
                      placeholder="Internal notes (optional)..."
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowReviewModal(false);
                      setSelectedProfile(null);
                      setReviewData({ rejectionReason: '', adminNotes: '' });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleReject(selectedProfile.id)}
                    className="flex-1"
                  >
                    Reject Profile
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Profile Modal */}
        {showViewModal && selectedProfile && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Law Enforcement Profile Details
                  </h2>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(selectedProfile.verificationStatus)}
                  </div>
                </div>
                <button
                  onClick={closeViewModal}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* User Information */}
                  <Card className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</label>
                        <p className="text-gray-900 dark:text-white">{selectedProfile.userEmail || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Profile ID</label>
                        <p className="text-gray-900 dark:text-white">{selectedProfile.id}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</label>
                        <p className="text-gray-900 dark:text-white">
                          {selectedProfile.createdAt ? new Date(selectedProfile.createdAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</label>
                        <p className="text-gray-900 dark:text-white">
                          {selectedProfile.updatedAt ? new Date(selectedProfile.updatedAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Office Information */}
                  <Card className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Office Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Department</label>
                        <p className="text-gray-900 dark:text-white">{selectedProfile.departmentName || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Rank/Position</label>
                        <p className="text-gray-900 dark:text-white">{selectedProfile.rankPosition || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Office Address</label>
                        <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                          {selectedProfile.officeAddress || 'Not provided'}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Contact Information */}
                  <Card className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">WhatsApp Number</label>
                        <p className="text-gray-900 dark:text-white">{selectedProfile.whatsappNumber || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Telegram Number</label>
                        <p className="text-gray-900 dark:text-white">{selectedProfile.telegramNumber || 'Not provided'}</p>
                      </div>
                    </div>
                  </Card>

                  {/* Verification Information */}
                  <Card className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Verification Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Government Email</label>
                        <p className="text-gray-900 dark:text-white">{selectedProfile.governmentEmail || 'Not provided'}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Email Verified</label>
                        <Badge variant={selectedProfile.governmentEmailVerified ? 'success' : 'danger'}>
                          {selectedProfile.governmentEmailVerified ? 'Verified' : 'Not Verified'}
                        </Badge>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Government ID Document</label>
                        {selectedProfile.governmentIdFile ? (
                          <div className="mt-2">
                            <a
                              href={selectedProfile.governmentIdFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                            >
                              View Document
                            </a>
                          </div>
                        ) : (
                          <p className="text-gray-900 dark:text-white">Not uploaded</p>
                        )}
                      </div>
                    </div>
                  </Card>

                  {/* Review Information */}
                  {(selectedProfile.reviewedBy || selectedProfile.reviewedAt || selectedProfile.rejectionReason) && (
                    <Card className="p-4 md:col-span-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Review Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedProfile.reviewedBy && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Reviewed By</label>
                            <p className="text-gray-900 dark:text-white">{selectedProfile.reviewedBy}</p>
                          </div>
                        )}
                        {selectedProfile.reviewedAt && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Reviewed At</label>
                            <p className="text-gray-900 dark:text-white">
                              {new Date(selectedProfile.reviewedAt).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedProfile.rejectionReason && (
                          <div className="md:col-span-2">
                            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Rejection Reason</label>
                            <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                              {selectedProfile.rejectionReason}
                            </p>
                          </div>
                        )}
                        {selectedProfile.adminNotes && (
                          <div className="md:col-span-2">
                            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Admin Notes</label>
                            <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                              {selectedProfile.adminNotes}
                            </p>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  onClick={closeViewModal}
                >
                  Close
                </Button>
                {selectedProfile.verificationStatus === 'pending' && (
                  <div className="flex space-x-2">
                    <Button
                      variant="success"
                      onClick={() => {
                        closeViewModal();
                        handleApprove(selectedProfile.id);
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        closeViewModal();
                        openReviewModal(selectedProfile);
                      }}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
